"use client";


import { AlertCircle, Home, RefreshCw } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { reportClientError } from "@/lib/actions/client-error";

/**
 * Root error boundary for the Next.js application.
 * Catches errors that bubble up to the root layout.
 */
export default function ErrorBoundary({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        // L6 (Codex 2026-05-11): forward client-side errors into system_logs
        // via a server action. Same sink as server-side `logSystemError`,
        // so operators see both classes of error in one place. If/when a
        // dedicated tracking service (Sentry, OTel) is wired up, swap the
        // body of `reportClientError` — call sites stay the same.
        if (process.env.NODE_ENV === "production") {
            void reportClientError(
                error.message,
                error.digest,
                {
                    route: typeof window !== "undefined" ? window.location.pathname : undefined,
                    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
                },
            ).catch(() => {
                /* swallow: client-error logging must never break the
                   error boundary itself */
            });
        }
        console.error("Root error boundary caught:", error);
    }, [error]);

    const router = useRouter();

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="max-w-lg w-full">
                <CardHeader className="text-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                        <AlertCircle className="h-6 w-6 text-destructive" />
                    </div>
                    <CardTitle className="text-2xl">Anwendungsfehler</CardTitle>
                    <CardDescription>
                        Es ist ein kritischer Fehler aufgetreten. Wir entschuldigen uns für die Unannehmlichkeiten.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {process.env.NODE_ENV === "development" && (
                        <div className="rounded-md bg-muted p-4 overflow-auto">
                            <p className="text-sm font-medium text-muted-foreground mb-2">
                                Fehlerdetails (nur Entwicklungsmodus):
                            </p>
                            <pre className="text-xs text-muted-foreground whitespace-pre-wrap">
                                {error.message}
                                {error.digest && `\nDigest: ${error.digest}`}
                            </pre>
                        </div>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        <Button onClick={() => { router.refresh(); reset(); }} variant="default">
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Erneut versuchen
                        </Button>
                        <Button asChild variant="outline">
                            <Link href="/">
                                <Home className="mr-2 h-4 w-4" />
                                Zur Startseite
                            </Link>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
